import GlobalController from "../base-controllers/GlobalController.js";
import { getCsrfToken } from "../utils/csrf.js";
import { SSE_EVENT_PREFIX } from "./EventStream.js";
export const BROWSER_NOTIFICATIONS_CONTROLLER_ID = "BrowserNotifications";
/** Shows recent member-specific notifications through the browser notification UI. */
class BrowserNotifications extends GlobalController {
    constructor() {
        super(...arguments);
        this.destroyed = false;
        this.permissionChanged = () => {
            const previous = document.documentElement.dataset.browserNotificationPermission;
            this.updatePermissionState();
            if (this.permission === "granted" && previous !== "granted") {
                void this.deliverPendingNotifications();
            }
        };
        this.updatePermissionState = () => {
            document.documentElement.dataset.browserNotificationPermission =
                this.permission;
        };
        this.requestPermission = async (event) => {
            event.preventDefault();
            if (!("Notification" in window))
                return;
            try {
                await window.Notification.requestPermission();
                this.updatePermissionState();
                if (this.permission === "granted") {
                    await this.deliverPendingNotifications();
                }
            }
            catch (error) {
                document.documentElement.dataset.browserNotificationPermission = "error";
                this.handleError(error, "Requesting browser notifications");
            }
        };
        this.deliverPendingNotifications = async () => {
            const url = document.body?.dataset.browserNotificationsUrl;
            if (this.permission !== "granted" || !url)
                return;
            try {
                const response = await fetch(url, {
                    method: "POST",
                    credentials: "same-origin",
                    headers: { "X-CSRFToken": getCsrfToken() || "" },
                });
                if (!response.ok)
                    return;
                const data = (await response.json());
                const registration = await navigator.serviceWorker.ready;
                await Promise.all(data.notifications.map((notification) => registration.showNotification(notification.title, {
                    body: notification.body,
                    icon: "/static/img/icon-192x192.png",
                    badge: "/static/img/icon-192x192.png",
                    tag: `sajkla-notification-${notification.id}`,
                    data: {
                        url: notification.url,
                        readUrl: notification.read_url,
                    },
                })));
            }
            catch (error) {
                this.handleError(error, "Showing browser notifications");
            }
        };
    }
    init() {
        this.destroyed = false;
        this.updatePermissionState();
        void this.observePermission();
        this.on(window, "focus", this.permissionChanged);
        this.on(window, "pageshow", this.permissionChanged);
        this.on(document, "visibilitychange", () => {
            if (document.visibilityState === "visible")
                this.permissionChanged();
        });
        this.onGlobal(`${SSE_EVENT_PREFIX}notifications`, this.deliverPendingNotifications);
        this.onGlobal("click", this.requestPermission, {
            delegate: "[data-enable-browser-notifications]",
        });
        if (this.permission === "granted") {
            this.deliverPendingNotifications();
        }
    }
    destroy() {
        this.destroyed = true;
    }
    async observePermission() {
        if (!("Notification" in window) || !navigator.permissions?.query)
            return;
        try {
            const status = await navigator.permissions.query({
                name: "notifications",
            });
            if (!this.destroyed)
                this.on(status, "change", this.permissionChanged);
        }
        catch {
            // Some browsers cannot query notification permission. Focus/visibility
            // checks still pick up settings changes when returning to Sajkla.
        }
    }
    onNavigation() {
        this.updatePermissionState();
        if (this.permission === "granted") {
            this.deliverPendingNotifications();
        }
    }
    get permission() {
        return "Notification" in window
            ? window.Notification.permission
            : "unsupported";
    }
}
BrowserNotifications.id = BROWSER_NOTIFICATIONS_CONTROLLER_ID;
export default BrowserNotifications;
//# sourceMappingURL=BrowserNotifications.js.map